import React, { useState, useEffect, useCallback } from 'react';

const C = {
 accent:'#3AAF58', accentLight:'#0F2040', accentDark:'#16A34A',
 bg:'#0A1628', surface:'#0F2040',
 border:'rgba(30,58,95,.8)', border2:'rgba(30,58,95,1)',
 text:'#F0F2FF', text2:'#A0A8C0', text3:'#606880',
 amber:'#D4840F', amberBg:'rgba(245,158,11,.1)', amberBorder:'#F0D499',
 red:'#EF4444', redBg:'rgba(239,68,68,.1)', redBorder:'#F0B8B8',
 blue:'#FFFFFF', blueBg:'#EAF1FB',
 purple:'#6B48A8', purpleBg:'#F0EBFB',
 teal:'#1A7A7A', tealBg:'#E7F5F5',
 orange:'#E05A00', orangeBg:'#FEF0E6',
 pink:'#E91E63', pinkBg:'#FCE4EC',
 dark:'#0F1E14', dark2:'#1A3A22',
};
const inr = n => '₹'+Number(Math.round(n)||0).toLocaleString('en-IN');
const API = 'https://api.botanix.in/api';
// ── FETCH HOOK ────────────────────────────────────────────────────────────────
function useLive(endpoint, fallback, interval=30000) {
 const [data, setData] = useState(fallback);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);
 const [lastUpdated, setLastUpdated] = useState(null);

 const fetch_ = useCallback(async () => {
 try {
 const res = await fetch(`${API}${endpoint}`);
 if(!res.ok) throw new Error(`HTTP ${res.status}`);
 const json = await res.json();
 setData(json);
 setError(null);
 setLastUpdated(new Date());
 } catch(err) {
 setError(err.message);
 } finally {
 setLoading(false);
 }
 }, [endpoint]);

 useEffect(() => {
 fetch_();
 const timer = setInterval(fetch_, interval);
 return () => clearInterval(timer);
 }, [fetch_, interval]);

 return {data, loading, error, lastUpdated, refresh:fetch_};
}

// ── FALLBACK DATA (shown when DB not connected) ───────────────────────────────
const FALLBACK_KPIS = {
 rooms:{occupied:4,total:12,checkInsToday:2,checkOutsToday:1},
 revenue:{today:48500,month:1840000,target:2500000},
 bookings:{today:3},
 lowStock:8, pendingHousekeeping:3,
 overduePayments:576000,
 staff:{present:50,total:54},
 events:{today:2},
};

const FALLBACK_EVENTS = [
 {event_name:'TechCorp Day Out',event_type:'Corporate',pax_count:280,venue_name:'Adventure Garden',status:'confirmed'},
 {event_name:'GreenVille School',event_type:'School Picnic',pax_count:200,venue_name:'Pilkhan Garden',status:'confirmed'},
];

const FALLBACK_REVENUE = [
 {month:'Jan',revenue:2540000},{month:'Feb',revenue:2130000},{month:'Mar',revenue:1500000},
 {month:'Apr',revenue:1365000},{month:'May',revenue:1905000},{month:'Jun',revenue:1050000},
 {month:'Jul',revenue:1170000},
];

const FALLBACK_ALERTS = [
 {id:1,tone:'red', msg:"Paneer stock critical — 40kg remaining, reorder at 60kg"},
 {id:2,tone:'amber',msg:"Room 103 housekeeping pending — 2hrs unassigned"},
 {id:3,tone:'amber',msg:"4 staff absent today without approval"},
];

const DEPTS = [
 {id:'sales_crm', name:'Sales CRM', value:'Sales Pipeline', c:C.red, g:`linear-gradient(135deg,#E05050,${C.red})`},
 {id:'bookings', name:'Bookings', value:'All Reservations', c:C.blue, g:`linear-gradient(135deg,#3070D0,${C.blue})`},
 {id:'room_pms', name:'Room PMS', value:'Room Calendar', c:C.purple, g:`linear-gradient(135deg,#8060C0,${C.purple})`},
 {id:'events', name:'Events', value:'Events & Weddings', c:C.accent, g:`linear-gradient(135deg,${C.accent},${C.accentDark})`},
 {id:'inventory', name:'Kitchen & Stock', value:'Inventory & Menu', c:C.amber, g:`linear-gradient(135deg,#F0A030,${C.amber})`},
 {id:'housekeeping',name:'Housekeeping', value:'Room Management', c:C.teal, g:`linear-gradient(135deg,#2A8A8A,${C.teal})`},
 {id:'horticulture',name:'Horticulture', value:'Garden & Plants', c:'#558B2F', g:`linear-gradient(135deg,#558B2F,#3A6020)`},
 {id:'construction',name:'Construction', value:'Projects & Civils', c:C.orange, g:`linear-gradient(135deg,#F07030,${C.orange})`},
 {id:'accounts', name:'Accounts & GST', value:'Finance & Billing', c:C.blue, g:`linear-gradient(135deg,#3070D0,${C.blue})`},
 {id:'hr', name:'HR & Staff', value:'People & Payroll', c:C.purple, g:`linear-gradient(135deg,#8060C0,${C.purple})`},
 {id:'marketing', name:'Marketing', value:'Campaigns & Leads', c:C.pink, g:`linear-gradient(135deg,#E050A0,${C.pink})`},
 {id:'reports', name:'Reports', value:'Analytics & BI', c:C.teal, g:`linear-gradient(135deg,#2A8A8A,${C.teal})`},
];

// ── STATUS DOT ────────────────────────────────────────────────────────────────
const LiveDot = ({color=C.accent}) => (
 <span style={{display:'inline-block',width:7,height:7,borderRadius:'50%',background:color,boxShadow:`0 0 4px ${color}`,animation:'pulse 2s infinite'}}/>
);

export default function LiveDashboard({setTab}) {
 const [dismissed, setDismissed] = useState([]);

 const kpis = useLive('/dashboard/kpis', FALLBACK_KPIS, 15000);
 const events = useLive('/dashboard/events/today', FALLBACK_EVENTS, 30000);
 const monthly = useLive('/dashboard/revenue/monthly', FALLBACK_REVENUE, 60000);
 const alerts = useLive('/dashboard/alerts', FALLBACK_ALERTS, 20000);
 const revBreak = useLive('/dashboard/revenue/breakdown',[], 60000);

 const d = kpis.data;
 const visAlerts = (alerts.data||[]).filter(a=>!dismissed.includes(a.id));
 const maxRev = Math.max(...(monthly.data||[]).map(m=>parseFloat(m.revenue||0)), 1);
 const revPct = Math.round((d.revenue?.today_revenue||0)/(d.revenue?.target||2500000)*100);
 const occPct = d.rooms?.total>0 ? Math.round((d.rooms?.occupied||0)/d.rooms.total*100) : 0;
 const isLive = !kpis.error;

 return (
 <div style={{fontFamily:'Inter,sans-serif',color:C.text,background:C.bg,minHeight:'100vh'}}>
 <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>

 {/* ── HERO BANNER ── */}
 <div style={{background:`linear-gradient(135deg,${C.dark} 0%,${C.dark2} 55%,#0A2010 100%)`,padding:'28px 32px',position:'relative',overflow:'hidden'}}>
 <div style={{position:'absolute',top:-80,right:-60,width:320,height:320,borderRadius:'50%',background:'rgba(58,175,88,.05)',pointerEvents:'none'}}/>
 <div style={{position:'absolute',bottom:-100,right:200,width:240,height:240,borderRadius:'50%',background:'rgba(58,175,88,.04)',pointerEvents:'none'}}/>
 <div style={{position:'relative'}}>

 {/* Title */}
 <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:24}}>
 <div>
 <div style={{fontSize:10,color:'rgba(255,255,255,.3)',textTransform:'uppercase',letterSpacing:'.16em',marginBottom:6}}>
 Botanix Nature Resort — Live Command Centre
 </div>
 <div style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:34,fontWeight:800,color:'#fff',letterSpacing:'-.03em',marginBottom:4}}>
 Botanix Command Centre
 </div>
 <div style={{display:'flex',alignItems:'center',gap:10}}>
 <div style={{fontSize:13,color:'rgba(255,255,255,.35)'}}>
 {new Date().toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
 </div>
 <div style={{display:'flex',alignItems:'center',gap:5,padding:'3px 10px',background:'rgba(58,175,88,.15)',borderRadius:99,border:'1px solid rgba(58,175,88,.3)'}}>
 <LiveDot color={isLive?C.accent:'#FF6B6B'}/>
 <span style={{fontSize:10,fontWeight:600,color:isLive?C.accent:'#FF6B6B'}}>{isLive?'LIVE DATA':'DEMO DATA'}</span>
 </div>
 {kpis.lastUpdated&&<span style={{fontSize:10,color:'rgba(255,255,255,.2)'}}>Updated {kpis.lastUpdated.toLocaleTimeString('en-IN')}</span>}
 </div>
 </div>
 <div style={{textAlign:'right'}}>
 <div style={{fontSize:10,color:'rgba(255,255,255,.3)',textTransform:'uppercase',letterSpacing:'.1em',marginBottom:6}}>Monthly Revenue</div>
 <div style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:36,fontWeight:800,color:C.accent,letterSpacing:'-.03em'}}>
 {kpis.loading?'Loading…':inr(d.revenue?.today_revenue||0)}
 </div>
 <div style={{display:'flex',alignItems:'center',gap:8,justifyContent:'flex-end',marginTop:6}}>
 <div style={{width:120,height:6,background:'rgba(255,255,255,.1)',borderRadius:99}}>
 <div style={{height:6,width:`${Math.min(revPct,100)}%`,background:`linear-gradient(90deg,${C.accent},#50D080)`,borderRadius:99,boxShadow:`0 0 8px ${C.accent}`,transition:'width .5s'}}/>
 </div>
 <span style={{fontSize:12,fontWeight:700,color:C.accent}}>{revPct}% of {inr(d.revenue?.target||2500000)}</span>
 </div>
 </div>
 </div>

 {/* 8 KPIs */}
 <div style={{display:'grid',gridTemplateColumns:'repeat(8,1fr)',gap:10,marginBottom:20}}>
 {[
 {l:'Room Occupancy', v:kpis.loading?'…':`${d.rooms?.occupied||0}/${d.rooms?.total||12}`, s:`${occPct}% occupied`, ic:'',c:'#64B5F6', t:'room_pms'},
 {l:'Today Revenue', v:kpis.loading?'…':inr(d.revenue?.today||0), s:'Live today', ic:'₹', c:C.accent, t:'accounts'},
 {l:'Today Bookings', v:kpis.loading?'…':String(d.bookings?.today||0), s:'New today', ic:'',c:'#CE93D8', t:'bookings'},
 {l:'Today Events', v:kpis.loading?'…':String(d.events?.today||0), s:'Active events', ic:'',c:'#81C784', t:'events'},
 {l:'Low Stock', v:kpis.loading?'…':String(d.lowStock||0), s:'Need reorder', ic:'', c:'#FFB347', t:'inventory'},
 {l:'Check-ins', v:kpis.loading?'…':String(d.rooms?.checkInsToday||0), s:'Arriving today', ic:'',c:'#80CBC4', t:'room_pms'},
 {l:'Alerts', v:String(visAlerts.length), s:'Need attention', ic:'',c:'#EF9A9A', t:'dashboard'},
 {l:'Staff Present', v:kpis.loading?'…':`${d.staff?.present||0}/${d.staff?.total||54}`, s:'Today', ic:'',c:'#B39DDB', t:'hr'},
 ].map(k=>(
 <div key={k.l} onClick={()=>setTab(k.t)}
 style={{padding:'14px 12px',background:'rgba(255,255,255,.06)',borderRadius:14,border:'1px solid rgba(255,255,255,.08)',cursor:'pointer',textAlign:'center',transition:'all .2s'}}>
 <div style={{fontSize:22,marginBottom:6}}>{k.ic}</div>
 <div style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:22,fontWeight:800,color:k.c,letterSpacing:'-.02em',marginBottom:3}}>{k.v}</div>
 <div style={{fontSize:9,color:'rgba(255,255,255,.35)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:2}}>{k.l}</div>
 <div style={{fontSize:10,color:k.c,fontWeight:600,opacity:.8}}>{k.s}</div>
 </div>
 ))}
 </div>

 {/* Mini bar chart + donuts */}
 <div style={{display:'grid',gridTemplateColumns:'1fr auto auto',gap:20,alignItems:'end'}}>
 <div>
 <div style={{fontSize:10,color:'rgba(255,255,255,.3)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8}}>
 Monthly Revenue Trend {new Date().getFullYear()} <LiveDot/>
 </div>
 <div style={{display:'flex',alignItems:'flex-end',gap:4,height:48}}>
 {(monthly.data||FALLBACK_REVENUE).map((d,i)=>{
 const r = parseFloat(d.revenue||d.r||0);
 const isLast = i===(monthly.data||FALLBACK_REVENUE).length-1;
 return (
 <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
 <div style={{width:'100%',background:isLast?C.accent:C.accent+'40',borderRadius:'3px 3px 0 0',height:`${Math.max(4,(r/maxRev)*44)}px`,transition:'height .5s'}}/>
 <div style={{fontSize:7,color:'rgba(255,255,255,.3)',fontWeight:600}}>{d.month||d.m}</div>
 </div>
 );
 })}
 </div>
 </div>
 {[[occPct,'Occupancy',C.accent],[revPct,'vs Target','#64B5F6']].map(([p,l,c])=>(
 <div key={l} style={{textAlign:'center'}}>
 <div style={{fontSize:10,color:'rgba(255,255,255,.3)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>{l}</div>
 <svg width={60} height={60} viewBox="0 0 60 60">
 <circle cx={30} cy={30} r={24} fill="none" stroke="rgba(255,255,255,.1)" strokeWidth={6}/>
 <circle cx={30} cy={30} r={24} fill="none" stroke={c} strokeWidth={6}
 strokeDasharray={2*Math.PI*24} strokeDashoffset={2*Math.PI*24*(1-Math.min(p,100)/100)}
 strokeLinecap="round" style={{transform:'rotate(-90deg)',transformOrigin:'center',transition:'stroke-dashoffset .5s'}}/>
 <text x={30} y={34} textAnchor="middle" fontSize={11} fontWeight={700} fill={c}>{p}%</text>
 </svg>
 </div>
 ))}
 </div>
 </div>
 </div>

 {/* ── SECOND GREEN BAND ── */}
 <div style={{background:`linear-gradient(135deg,#1A4A20,#243828)`,padding:'12px 32px',display:'flex',gap:8,overflowX:'auto',alignItems:'center'}}>
 <span style={{fontSize:10,color:'rgba(255,255,255,.3)',textTransform:'uppercase',letterSpacing:'.1em',whiteSpace:'nowrap',marginRight:8}}>Quick Navigate:</span>
 {[
 {l:'Sales CRM',t:'sales_crm',ic:''},{l:'Bookings',t:'bookings',ic:''},
 {l:'Room PMS',t:'room_pms',ic:''},{l:'Events',t:'events',ic:''},
 {l:'Kitchen',t:'kitchen_mgmt',ic:''},{l:'Housekeeping',t:'housekeeping',ic:''},
 {l:'Accounts',t:'accounts',ic:''},{l:'HR',t:'hr',ic:''},
 {l:'Reports',t:'reports',ic:''},{l:'Horticulture',t:'horticulture',ic:''},
 {l:'Construction',t:'construction',ic:''},{l:'Marketing',t:'marketing',ic:''},
 ].map(q=>(
 <button key={q.t} onClick={()=>setTab(q.t)}
 style={{padding:'6px 14px',borderRadius:99,background:'rgba(255,255,255,.07)',border:'1px solid rgba(255,255,255,.12)',color:'rgba(255,255,255,.7)',fontSize:11,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap',display:'flex',alignItems:'center',gap:5}}>
 {q.ic} {q.l}
 </button>
 ))}
 <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:6}}>
 <LiveDot color={isLive?C.accent:'#FF6B6B'}/>
 <span style={{fontSize:10,color:'rgba(255,255,255,.3)'}}>
 {isLive?`Live · Auto-refresh every 15s`:`Demo mode · Backend offline`}
 </span>
 </div>
 </div>

 <div style={{padding:'24px 32px'}}>

 {/* ── DB ERROR NOTICE ── */}
 {kpis.error&&(
 <div style={{padding:'12px 18px',background:C.amberBg,border:`1px solid ${C.amberBorder}`,borderRadius:12,marginBottom:20,fontSize:12,color:C.amber}}>
 <strong>Backend not connected</strong> — showing demo data. Start backend with <code>node server.js</code> to see live data.
 </div>
 )}

 {/* ── ALERTS ── */}
 {visAlerts.length>0&&(
 <div style={{marginBottom:24}}>
 <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
 <div style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:16,fontWeight:800,color:C.text}}>
 Active Alerts <LiveDot/>
 <span style={{fontSize:13,fontWeight:600,color:C.red,background:C.redBg,padding:'2px 10px',borderRadius:99,marginLeft:8}}>{visAlerts.length}</span>
 </div>
 <button onClick={()=>setDismissed((alerts.data||[]).map(a=>a.id))} style={{fontSize:12,color:C.text3,background:'none',border:`1px solid ${C.border2}`,padding:'5px 14px',borderRadius:8,cursor:'pointer'}}>Dismiss All</button>
 </div>
 {visAlerts.map(a=>(
 <div key={a.id} style={{display:'flex',alignItems:'center',gap:12,padding:'11px 16px',background:a.tone==='red'?C.redBg:C.amberBg,borderRadius:10,border:`1px solid ${a.tone==='red'?C.redBorder:C.amberBorder}`,marginBottom:8}}>
 <span style={{fontSize:16}}>{a.icon}</span>
 <span style={{fontSize:12,color:a.tone==='red'?C.red:C.amber,flex:1,fontWeight:500}}>{a.msg}</span>
 <button onClick={()=>setDismissed(p=>[...p,a.id])} style={{background:'none',border:'none',cursor:'pointer',color:a.tone==='red'?C.red:C.amber,fontSize:16,opacity:.6}}></button>
 </div>
 ))}
 </div>
 )}

 {/* ── TODAY'S EVENTS + ROOMS ── */}
 <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:24}}>
 {/* Events */}
 <div style={{borderRadius:16,overflow:'hidden',boxShadow:'0 4px 16px rgba(0,0,0,.08)'}}>
 <div style={{background:`linear-gradient(135deg,${C.accent},${C.accentDark})`,padding:'16px 20px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
 <div>
 <div style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:15,fontWeight:800,color:'#fff',display:'flex',alignItems:'center',gap:8}}>
 Today's Events <LiveDot/>
 </div>
 <div style={{fontSize:11,color:'rgba(255,255,255,.6)',marginTop:2}}>{(events.data||[]).length} events today</div>
 </div>
 <button onClick={()=>setTab('events')} style={{padding:'6px 14px',background:'rgba(255,255,255,.2)',border:'none',borderRadius:9,color:'#fff',fontSize:11,fontWeight:600,cursor:'pointer'}}>View All →</button>
 </div>
 <div style={{background:C.surface,border:`1px solid ${C.border}`,borderTop:'none',borderRadius:'0 0 16px 16px'}}>
 {(events.data||FALLBACK_EVENTS).map((ev,i)=>(
 <div key={i} onClick={()=>setTab('events')} style={{padding:'13px 18px',borderBottom:i<(events.data||FALLBACK_EVENTS).length-1?`1px solid ${C.border}`:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:14}}>
 <div style={{width:44,height:44,borderRadius:12,background:C.accent+'15',border:`1.5px solid ${C.accent}33`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>
 {ev.event_type==='Wedding'?'':ev.event_type==='Corporate'?'':ev.event_type==='School Picnic'?'':''}
 </div>
 <div style={{flex:1}}>
 <div style={{fontWeight:700,fontSize:13,color:C.text,marginBottom:2}}>{ev.event_name}</div>
 <div style={{fontSize:11,color:C.text3}}>{ev.venue_name} · {ev.pax_count} pax</div>
 </div>
 <span style={{padding:'3px 10px',borderRadius:99,fontSize:10,fontWeight:600,color:C.accent,background:C.accentLight}}>{ev.status}</span>
 </div>
 ))}
 {(events.data||[]).length===0&&!events.loading&&<div style={{padding:'24px',textAlign:'center',color:C.text3,fontSize:13}}>No events today</div>}
 </div>
 </div>

 {/* Revenue Breakdown */}
 <div style={{borderRadius:16,overflow:'hidden',boxShadow:'0 4px 16px rgba(0,0,0,.08)'}}>
 <div style={{background:`linear-gradient(135deg,${C.dark2},#243828)`,padding:'16px 20px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
 <div style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:15,fontWeight:800,color:'#fff',display:'flex',alignItems:'center',gap:8}}>
 Revenue Breakdown <LiveDot/>
 </div>
 <button onClick={()=>setTab('accounts')} style={{padding:'6px 14px',background:'rgba(255,255,255,.15)',border:'none',borderRadius:9,color:'#fff',fontSize:11,fontWeight:600,cursor:'pointer'}}>Full Report →</button>
 </div>
 <div style={{background:C.surface,border:`1px solid ${C.border}`,borderTop:'none',borderRadius:'0 0 16px 16px',padding:'16px 20px'}}>
 {([
 ...(revBreak.data||[]),
 ...(revBreak.data?.length===0?[{category:'Rooms',revenue:840000},{category:'F&B',revenue:320000},{category:'Events',revenue:1200000},{category:'Activities',revenue:180000}]:[])
 ]).map((item,i)=>{
 const colors=[C.blue,C.amber,C.accent,C.purple,C.teal];
 const c=colors[i%colors.length];
 const rev=parseFloat(item.revenue||0);
 const total=parseFloat(d.revenue?.today_revenue||2540000);
 const pct=Math.round(rev/total*100);
 return (
 <div key={item.category} style={{marginBottom:12}}>
 <div style={{display:'flex',justifyContent:'space-between',fontSize:12,marginBottom:4}}>
 <span style={{fontWeight:600,color:C.text}}>{item.category}</span>
 <div style={{display:'flex',gap:8}}>
 <span style={{fontWeight:700,color:c}}>{pct}%</span>
 <span style={{color:C.text3}}>{inr(rev/100000)}L</span>
 </div>
 </div>
 <div style={{height:6,background:C.border,borderRadius:99}}>
 <div style={{height:6,width:`${pct}%`,background:c,borderRadius:99,transition:'width .5s'}}/>
 </div>
 </div>
 );
 })}
 <div style={{marginTop:14,padding:'10px 14px',background:C.accentLight,borderRadius:9,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
 <span style={{fontSize:12,color:C.text2,fontWeight:600}}>Total This Month</span>
 <span style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:18,fontWeight:800,color:C.accent}}>{inr(d.revenue?.today_revenue||0)}</span>
 </div>
 </div>
 </div>
 </div>

 {/* ── DEPARTMENT GRID ── */}
 <div style={{marginBottom:24}}>
 <div style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:18,fontWeight:800,color:C.text,marginBottom:16}}>
 Department Status &nbsp;<span style={{fontSize:12,fontWeight:500,color:C.text3}}>Click any department to open</span>
 </div>
 <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14}}>
 {DEPTS.map(dept=>{
 // Inject live values where available
 let liveValue = dept.value;
 let liveSub = '';
 if(dept.id==='room_pms') { liveValue=`${d.rooms?.occupied||0}/${d.rooms?.total||12} Occupied`; liveSub=`${occPct}% Occupancy`; }
 if(dept.id==='inventory') { liveValue=`${d.lowStock||0} Low Stock`; liveSub='Need Reorder'; }
 if(dept.id==='housekeeping') { liveValue=`${d.pendingHousekeeping||0} Pending`; liveSub='Tasks Today'; }
 if(dept.id==='hr') { liveValue=`${d.staff?.present||0}/${d.staff?.total||54} Present`; liveSub='Today'; }
 if(dept.id==='accounts') { liveValue=inr(d.overduePayments||0); liveSub='Outstanding'; }
 return (
 <div key={dept.id} onClick={()=>setTab(dept.id)} style={{borderRadius:14,overflow:'hidden',cursor:'pointer',boxShadow:'0 3px 12px rgba(0,0,0,.08)',border:`1px solid ${dept.c}22`,transition:'all .2s'}}>
 <div style={{background:dept.g,padding:'14px 16px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
 <div style={{fontSize:28}}>{dept.icon}</div>
 <div style={{background:'rgba(255,255,255,.15)',borderRadius:99,padding:'3px 10px',fontSize:11,fontWeight:700,color:'#fff'}}>Open →</div>
 </div>
 <div style={{background:C.surface,padding:'12px 16px',borderTop:`3px solid ${dept.c}`}}>
 <div style={{fontWeight:800,fontSize:14,color:C.text,marginBottom:3}}>{dept.name}</div>
 <div style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:15,fontWeight:800,color:dept.c,marginBottom:2}}>{liveValue}</div>
 {liveSub&&<div style={{fontSize:11,color:C.text3}}>{liveSub}</div>}
 </div>
 </div>
 );
 })}
 </div>
 </div>

 {/* ── NEEDS ATTENTION ── */}
 <div style={{borderRadius:16,overflow:'hidden',boxShadow:'0 4px 16px rgba(0,0,0,.08)'}}>
 <div style={{background:`linear-gradient(135deg,${C.red},#A02020)`,padding:'14px 20px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
 <div style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:14,fontWeight:800,color:'#fff',display:'flex',alignItems:'center',gap:8}}> Needs Attention <LiveDot color="#FF6B6B"/></div>
 <span style={{fontSize:11,color:'rgba(255,255,255,.5)'}}>Click to go to module</span>
 </div>
 <div style={{background:C.surface,border:`1px solid ${C.border}`,borderTop:'none',borderRadius:'0 0 16px 16px',padding:'16px 20px',display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
 {(visAlerts.slice(0,6).length>0?visAlerts.slice(0,6):[
 {id:'hk', msg:'Check housekeeping tasks', tone:'amber', tab:'housekeeping'},
 {id:'inv', msg:'Check inventory levels', tone:'red', tab:'inventory'},
 {id:'hr', msg:'Check staff attendance', tone:'amber', tab:'hr'},
 {id:'acc', msg:'Check pending payments', tone:'amber', tab:'accounts'},
 {id:'hort',msg:'Check horticulture tasks', tone:'amber', tab:'horticulture'},
 {id:'con', msg:'Check construction status',tone:'amber', tab:'construction'},
 ]).map((a,i)=>(
 <div key={a.id||i} onClick={()=>setTab(a.tab||'dashboard')} style={{display:'flex',gap:8,padding:'10px 12px',background:C.bg,borderRadius:10,border:`1px solid ${a.tone==='red'?C.redBorder:C.amberBorder}`,cursor:'pointer',alignItems:'flex-start',transition:'all .15s'}}>
 <span style={{fontSize:16,flexShrink:0}}>{a.icon||''}</span>
 <span style={{fontSize:11,color:C.text,flex:1,lineHeight:1.4}}>{a.msg}</span>
 <span style={{fontSize:12,color:a.tone==='red'?C.red:C.amber,fontWeight:700}}>→</span>
 </div>
 ))}
 </div>
 </div>
 </div>
 </div>
 );
}
